import { createAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  FetchLinkRequest,
  GenerateLinkRequest,
  GenerateLinkResponse,
  NDA,
  UpdateLinkRequest,
  FetchingLinkResponse,
  DeleteLinkRequest,
  GenrateLinkErrorResponse,
  NDATemplatesErrorResponse,
  UpdateLinkErrorResponse,
  FetchingLinkErrorResponse,
  DeleteLinkErrorResponse,
} from "src/types/link";
import {
  FETCH_NDA_TEMPLATES_REQUEST,
  FETCHING_NDA_TEMPLATES_FAILED,
  GENERATE_LINK_FAILED,
  GENERATE_LINK_REQUEST,
  GENERATE_LINK_SUCCEEDED,
  FETCHING_NDA_TEMPLATES_SUCCEEDED,
  UPDATE_LINK_REQUEST,
  UPDATING_LINK_FAILED,
  UPDATING_LINK_SUCCEEDED,
  FETCH_ALL_LINKS_REQUEST,
  FETCHING_ALL_LINKS_SUCCEEDED,
  FETCHING_ALL_LINKS_FAILED,
  DELETE_LINK_REQUEST,
  DELETING_LINK_SUCCEEDED,
  DELETING_LINK_FAILED,
  INVALIDATE_ALL_LINK_ITEMS,
  INVALIDATE_LINK_UPDATIONS,
  RESET_GENERATE_LINK_STATE,
} from "./actionTypes";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import _ from "lodash";

const apiUrl = `${process.env.REACT_APP_API_URL}`;
const COLLECTIONS_BASE_URL = `${apiUrl}/api/2.0/collections`;
const ASSETS_BASE_URL = `${apiUrl}/api/2.0/assets`;
const NDA_BASE_URL = `${apiUrl}/api/2.0/nda-templates`;
const LINK_BASE_URL = `${apiUrl}/api/2.0/links`;

export const generateLink = createAction<GenerateLinkRequest>(
  GENERATE_LINK_REQUEST,
);
export const generateLinkSucceeded = createAction<GenerateLinkResponse>(
  GENERATE_LINK_SUCCEEDED,
);
export const generateLinkFailed =
  createAction<GenrateLinkErrorResponse>(GENERATE_LINK_FAILED);

const generateLinkSaga = function* (
  action: ReturnType<typeof generateLink>,
): any {
  const request = {
    type: action.payload.type,
    email: action.payload.email,
    utmCampaign: action.payload.utmCampaign,
  };
  let baseUrl: unknown = null;

  if (action.payload.entityType === "asset") {
    baseUrl = ASSETS_BASE_URL;
  } else {
    baseUrl = COLLECTIONS_BASE_URL;
  }

  const generateLinkApi = async () => {
    return axios({
      method: "POST",
      url: `${baseUrl}/${action.payload.entityID}/link`,
      data: request,
    }).then((response) => response.data);
  };

  try {
    let response = yield call(generateLinkApi);
    yield put(generateLinkSucceeded(response));
  } catch (error) {
    yield put(generateLinkFailed({ error: error }));
  }
};

export const fetchNDARequest = createAction(FETCH_NDA_TEMPLATES_REQUEST);
export const fetchNDASucceeded = createAction<NDA[]>(
  FETCHING_NDA_TEMPLATES_SUCCEEDED,
);
export const fetchNDAFailed = createAction<NDATemplatesErrorResponse>(
  FETCHING_NDA_TEMPLATES_FAILED,
);
const fetchNDASaga = function* (
  action: ReturnType<typeof fetchNDARequest>,
): any {
  const fetchNDAApi = async () => {
    return axios({
      method: "GET",
      url: NDA_BASE_URL,
    }).then((response) => response.data);
  };
  try {
    const response = yield call(fetchNDAApi);
    yield put(fetchNDASucceeded(response));
  } catch (error) {
    yield put(fetchNDAFailed({ error: error }));
  }
};

export const fetchAllLinks = createAction<FetchLinkRequest>(
  FETCH_ALL_LINKS_REQUEST,
);
export const fetchAllLinksSucceeded = createAction<FetchingLinkResponse>(
  FETCHING_ALL_LINKS_SUCCEEDED,
);
export const fetchAllLinksFailed = createAction<FetchingLinkErrorResponse>(
  FETCHING_ALL_LINKS_FAILED,
);
const fetchAllLinksSaga = function* (
  action: ReturnType<typeof fetchAllLinks>,
): any {
  let baseUrl: unknown = null;
  if (action.payload.entityType === "asset") {
    baseUrl = ASSETS_BASE_URL;
  } else {
    baseUrl = COLLECTIONS_BASE_URL;
  }
  const fetchAllLinksApi = async () => {
    return axios({
      method: "GET",
      url: `${baseUrl}/${action.payload.entityID}/links`,
      params: {
        page: action.payload.page,
      },
    }).then((response) => response.data);
  };
  try {
    const response = yield call(fetchAllLinksApi);
    yield put(fetchAllLinksSucceeded(response));
  } catch (error) {
    yield put(fetchAllLinksFailed({ error: error }));
  }
};

export const updateLink = createAction<UpdateLinkRequest>(UPDATE_LINK_REQUEST);
export const updateLinkSucceeded = createAction<GenerateLinkResponse>(
  UPDATING_LINK_SUCCEEDED,
);
export const updateLinkFailed = createAction<{
  id: string;
  error: UpdateLinkErrorResponse;
}>(UPDATING_LINK_FAILED);

const updateLinkSaga = function* (action: ReturnType<typeof updateLink>): any {
  const updateLinkApi = async () => {
    return axios({
      method: "PUT",
      url: `${LINK_BASE_URL}/${action.payload.id}`,
      data: action.payload.request,
    }).then((response) => response.data);
  };
  try {
    let response = yield call(updateLinkApi);
    yield put(updateLinkSucceeded(response));
  } catch (error) {
    yield put(
      updateLinkFailed({
        id: action.payload.id,
        error: error as UpdateLinkErrorResponse,
      }),
    );
  }
};

export const deleteLinkRequest =
  createAction<DeleteLinkRequest>(DELETE_LINK_REQUEST);
export const deleteLinkSucceeded = createAction<DeleteLinkRequest>(
  DELETING_LINK_SUCCEEDED,
);
export const deleteLinkFailed = createAction<{
  id: string;
  error: DeleteLinkErrorResponse;
}>(DELETING_LINK_FAILED);

const deleteLinkSaga = function* (
  action: ReturnType<typeof deleteLinkRequest>,
): any {
  const deleteLinkApi = async () => {
    return axios({
      method: "DELETE",
      url: `${LINK_BASE_URL}/${action.payload.id}`,
    }).then((response) => response.data);
  };
  try {
    yield call(deleteLinkApi);
    yield put(deleteLinkSucceeded(action.payload));
  } catch (error) {
    yield put(
      deleteLinkFailed({
        id: action.payload.id,
        error: error as DeleteLinkErrorResponse,
      }),
    );
  }
};

export const invalidateAllLinks = createAction(INVALIDATE_ALL_LINK_ITEMS);
export const invalidateAllUpdations = createAction<{ id: string }>(
  INVALIDATE_LINK_UPDATIONS,
);
export const resetGenerateLinks = createAction(RESET_GENERATE_LINK_STATE);

export default function* linkSaga() {
  yield takeEvery(GENERATE_LINK_REQUEST, generateLinkSaga);
  yield takeEvery(FETCH_NDA_TEMPLATES_REQUEST, fetchNDASaga);
  yield takeEvery(UPDATE_LINK_REQUEST, updateLinkSaga);
  yield takeEvery(FETCH_ALL_LINKS_REQUEST, fetchAllLinksSaga);
  yield takeEvery(DELETE_LINK_REQUEST, deleteLinkSaga);
}
